import {
  Box,
  Flex
} from "@chakra-ui/react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react";
import { useHistory } from "react-router-dom";
import { Column } from "react-table";
import {
  format,
  isBefore
} from "date-fns";
import { Select } from "chakra-react-select";

import { useConversationsApi } from "src/api/conversations/conversations.api";
import { Header } from "src/components/shared/Header";
import {
  ITableInstance,
  Table
} from "src/components/table";
import { Text } from "src/components/text";
import {
  Conversation,
  ConversationMode
} from "src/api";
import { Avatar } from "src/components/avatar";

interface ConversationsAdminTableData {
  _id: Conversation["_id"]
  date: Conversation["date"]
  startTime: Conversation["startTime"]
  endTime: Conversation["endTime"]
  latencySeconds: Conversation["latencySeconds"]
  topics?: Conversation["topics"]
  mode: Conversation["mode"];
  creator?: string
  partner?: string
}

export const ConversationAdminPage: React.FC = () => {
  const history = useHistory();
  const [ conversationsRes, conversationsReq ] = useConversationsApi("GET_ADMIN_CONVERSATIONS");
  const [ conversationsTableData, setConversationsTableData ] = useState<ConversationsAdminTableData[]>([]);
  const [ selectedModeFilter, setSelectedModeFilter ] = React.useState<ConversationMode>();
  const TableRef = React.useRef<ITableInstance>(null);

  const columns = useMemo(
    (): Array<Column<ConversationsAdminTableData>> => [
      {
        Header: "Mode",
        accessor: "mode",
        Cell: props => (
          <div>
            {props.value === "voice" ? "Voice" : "Message"}
          </div>
        )
      },
      {
        Header: "Date",
        accessor: "date",
        Cell: props => (
          <div>
            {format(new Date(props.value), "yyyy/MM/dd")}
          </div>
        )
      },
      {
        Header: "Start Time",
        accessor: "startTime",
        Cell: props => (
          <div>
            {format(new Date(props.value), "HH:mm")}
          </div>
        )
      },
      {
        Header: "End Time",
        accessor: "endTime",
        Cell: props => (
          <div>
            {format(new Date(props.value), "HH:mm")}
          </div>
        )
      },
      {
        Header: "Latency",
        accessor: "latencySeconds"
      },
      {
        Header: "Creator",
        accessor: "creator",
        Cell: props => (
          <Avatar name={props.value || ""} />
        )
      },
      {
        Header: "Partner",
        accessor: "partner",
        Cell: props => (
          <Avatar name={props.value || ""} />
        )
      }
    ],
    []
  );

  React.useEffect(() => {
    TableRef.current?.reloadTable();
  }, [ selectedModeFilter ]);

  const handleFetch = useCallback((pageIndex: number, pageSize: number) => {
    const skip = pageIndex * pageSize;
    const filters: {mode?: string} = {};

    if (selectedModeFilter) {
      filters.mode = selectedModeFilter;
    }

    conversationsReq({
      skip,
      filter: filters,
      limit: pageSize
    });
  }, [ conversationsReq, selectedModeFilter ]);

  const handleClick = useCallback(conversation => {
    // If session hasn't passed go to conversation page
    if (isBefore(new Date(), new Date(conversation.original.endTime)))
      history.push(`/conversation/${conversation.original._id}`);
    else
      history.push(`/conversation/${conversation.original._id}/topic/${conversation.original.topics?.[ 0 ]._id}`);
  }, [ history ]);

  const pageCount = useMemo(() =>
    conversationsRes.data ?
      conversationsRes.data?.total < 10 ? 1 : Math.ceil(conversationsRes.data?.total / 10) :
      1,
  [ conversationsRes ]);

  useEffect(() => {
    if (conversationsRes.data) {
      const conversationsData = conversationsRes.data.results.map(conversation => {
        return {
          ...conversation,
          creator: conversation.participants?.find(p => p.creator)?.user.name,
          partner: conversation.participants?.find(p => !p.creator)?.user.name
        };
      });

      setConversationsTableData(conversationsData);
    }
  }, [ conversationsRes ]);

  return (
    <Box p={10}>
      <Header />

      <Box paddingY={10}>
        <Text as="h1">
          All Sessions
        </Text>

        <Box paddingY={4}>
          <Text as="p">
            View all sessions created by all users. This is an admin-only page.
          </Text>
        </Box>

        <Flex justifyContent="flex-end">
          <Box width={300}>
            <Select
              name="mode"
              options={[
                {
                  value: undefined,
                  label: "Please select"
                },
                {
                  value: ConversationMode.MESSAGE,
                  label: "Message"
                },
                {
                  value: ConversationMode.VOICE,
                  label: "Voice"
                }
              ]}
              placeholder="Filter by mode"
              closeMenuOnSelect
              onChange={(event: {value: ConversationMode; label: string}) => {
                setSelectedModeFilter(event.value);
              }}
              size="md"
            />
          </Box>
        </Flex>

        <Table
          columns={columns}
          fetchData={handleFetch}
          loading={conversationsRes.pending}
          pageCount={pageCount}
          handleClick={conversation => handleClick(conversation)}
          data={conversationsTableData || []}
        />

      </Box>

    </Box>
  );
};
